<template>
  <div class="pt-4">
    <v-form ref="atsWsForm">
      <v-row justify="center" align="center">

        <v-col cols="12" md="auto" class="mr-md-8">
          <ats-period-selector
            ref="periodSelector"
            :fecha-inicio.sync="data.fechaInicio"
            :fecha-fin.sync="data.fechaFin"
            :periodo-informado.sync="data.periodoInformado"
            :tipo-periodicidad.sync="data.tipoPeriodicidad"
          />
        </v-col>
        
        <v-col cols="12" md="auto" style="width: 420px;">
          <v-text-field
            v-model="data.bridgeUser"
            label="Usuario de acceso a Bridge WS"
            prepend-inner-icon="mdi-account"
            dense
            outlined
            :rules="[v => !!v || 'Usuario es requerido']"
          />

          <v-text-field
            v-model="data.bridgePassword"
            label="Clave de acceso a Bridge WS"
            prepend-inner-icon="mdi-lock"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            dense
            outlined
            :rules="[v => !!v || 'Clave es requerida']"
            @click:append="showPassword = !showPassword"
          />

          <v-btn
            :loading="loadingTestConnection"
            :disabled="!data.bridgeUser || !data.bridgePassword"
            color="red darken-1"
            block
            class="white--text mb-3"
            @click="testConnection"
          >
            <v-icon left>mdi-lan-check</v-icon>
            Test de Conexión a Bridge WS
          </v-btn>

          <v-btn
            :disabled="!connectionTestPassed || loadingWsProcess || hasActiveRows"
            :loading="loadingWsProcess"
            color="red darken-1"
            block
            class="white--text mb-3"
            @click="processWebService"
          >
            <v-icon left>
              mdi-cog-sync
            </v-icon>
            Generar ATS
          </v-btn>

          <v-alert
            v-if="hasActiveRows"
            type="info"
            text
            dense
            class="mb-3"
          >
            Ya hay una exportación en curso para esta compañía. Espere a que finalice antes de solicitar una nueva.
          </v-alert>

          <div v-if="loadingCredentials" class="mb-3">
            <div class="text-subtitle-2 text-center mb-2">
              Cargando credenciales Bridge WS...
            </div>
            <v-progress-linear
              color="red darken-1"
              indeterminate
              rounded
              height="6"
            />
          </div>

          <div v-if="loadingTestConnection" class="mb-3">
            <div class="text-subtitle-2 text-center mb-2">
              Probando conexión con Bridge WS...
            </div>
            <v-progress-linear
              color="red darken-1"
              indeterminate
              rounded
              height="6"
            />
          </div>

          <div v-if="loadingWsProcess" class="mb-3">
            <div class="text-subtitle-2 text-center mb-2">
              Solicitando exportación ATS...
            </div>
            <v-progress-linear
              color="red darken-1"
              indeterminate
              rounded
              height="6"
            />
          </div>

          <v-alert
            v-show="showAlertTest"
            :type="alertTestType"
            text
            dense
            class="mb-2"
          >
            {{ alertTestMessage }}
          </v-alert>

          <v-alert
            v-show="showAlert"
            :type="alertType"
            text
            dense
            class="mb-0"
          >
            {{ alertMessage }}
          </v-alert>
        </v-col>
      </v-row>
    </v-form>

    <v-divider class="my-6" />

    <v-card-title class="px-0">
      Exportaciones ATS
      <v-spacer />
      <v-btn
        :loading="loadingWsList"
        :disabled="loadingWsList"
        icon
        style="background-color:rgb(204 33 40);color:white !important"
        @click="refreshList"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <div
      v-if="isPolling"
      class="text-caption grey--text text-right mb-2"
    >
      <v-icon
        small
        color="grey"
      >
        mdi-sync
      </v-icon>
      Actualizando automáticamente...
    </div>

    <v-row
      v-if="loadingWsList && wsList.length === 0"
      class="fill-height my-4"
      align-content="center"
      justify="center"
    >
      <v-col cols="6">
        <v-col
          class="text-subtitle-1 text-center"
          cols="12"
        >
          Cargando exportaciones ATS
        </v-col>
        <v-progress-linear
          color="red darken-4"
          indeterminate
          rounded
          height="6"
        />
      </v-col>
    </v-row>

    <v-data-table
      v-else
      :headers="tableHeaders"
      :items="wsList"
      :footer-props="{
        'items-per-page-text': 'filas por pagina',
      }"
      no-data-text="No hay exportaciones registradas para esta compañía"
      class="elevation-1"
    >
      <template v-slot:[`item.PeriodoInformado`]="{ item }">
        {{ item.PeriodoInformado }}
        <span
          v-if="item.TipoPeriodicidad"
          class="grey--text text-caption"
        >
          ({{ formatTipoPeriodicidad(item.TipoPeriodicidad) }})
        </span>
      </template>

      <template v-slot:[`item.FechaInicio`]="{ item }">
        {{ formatDateOnly(item.FechaInicio) }} — {{ formatDateOnly(item.FechaFin) }}
      </template>

      <template v-slot:[`item.FechaSolicitud`]="{ item }">
        {{ formatDateTime(item.FechaSolicitud) }}
      </template>

      <template v-slot:[`item.Estado`]="{ item }">
        <v-chip
          :color="estadoColor(item.Estado)"
          dark
          small
          label
        >
          <v-icon
            v-if="isActiveEstado(item.Estado)"
            left
            x-small
          >
            mdi-progress-clock
          </v-icon>
          {{ formatEstado(item.Estado) }}
        </v-chip>
      </template>

      <template v-slot:[`item.acciones`]="{ item }">
        <v-btn
          v-if="item.Disponible"
          :loading="downloadingTrackingId === item.TrackingId"
          color="success darken-2"
          small
          dark
          @click="downloadRow(item)"
        >
          <v-icon
            left
            x-small
          >
            mdi-download
          </v-icon>
          Descargar
        </v-btn>

        <v-tooltip
          v-else-if="item.Estado === 'failed'"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="red darken-2"
              small
              dark
              v-bind="attrs"
              v-on="on"
              @click="showErrorDetail(item)"
            >
              <v-icon
                left
                x-small
              >
                mdi-alert-circle
              </v-icon>
              Ver error
            </v-btn>
          </template>
          <span>{{ item.Error || 'Error desconocido' }}</span>
        </v-tooltip>

        <span
          v-else
          class="grey--text text-caption"
        >
          <v-icon small>
            mdi-clock-outline
          </v-icon>
          En proceso
        </span>
      </template>
    </v-data-table>

    <v-dialog
      v-model="errorDialog"
      max-width="540"
    >
      <v-card>
        <v-toolbar
          dark
          color="red darken-1"
          dense
          flat
        >
          <v-icon left>
            mdi-alert-circle
          </v-icon>
          Detalle del error
        </v-toolbar>
        <v-card-text class="pt-4">
          <div class="mb-2">
            <strong>TrackingId:</strong> {{ errorDialogItem && errorDialogItem.TrackingId }}
          </div>
          <div class="mb-2">
            <strong>Período:</strong> {{ errorDialogItem && errorDialogItem.PeriodoInformado }}
          </div>
          <v-divider class="my-2" />
          <div style="white-space: pre-wrap; word-break: break-word;">
            {{ errorDialogItem && errorDialogItem.Error || 'Sin detalle de error.' }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="errorDialog = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { saveAs } from 'file-saver'
  import JSZip from 'jszip'
  import AtsPeriodSelector from './AtsPeriodSelector'

  const POLLING_INTERVAL_MS = 30000

  const getUserData = () => {
    return JSON.parse(atob(localStorage.getItem('uid')))
  }

  export default {
    name: 'AtsWebServiceProcessing',

    components: {
      AtsPeriodSelector,
    },

    data () {
      const userData = getUserData()

      return {
        data: {
          fechaInicio: '',
          fechaFin: '',
          periodoInformado: '',
          tipoPeriodicidad: '',
          bridgeUser: '',
          bridgePassword: '',
        },
        showPassword: false,
        loadingCredentials: false,
        connectionTestPassed: false,
        showAlert: false,
        alertMessage: '',
        alertType: 'success',
        showAlertTest: false,
        alertTestMessage: '',
        alertTestType: 'success',
        userIdCompania: userData.idcompania,
        pollingTimer: null,
        downloadingTrackingId: null,
        errorDialog: false,
        errorDialogItem: null,
        tableHeaders: [
          { text: 'Fecha Solicitud', value: 'FechaSolicitud', align: 'center' },
          { text: 'Período', value: 'PeriodoInformado', sortable: false, align: 'center' },
          { text: 'Rango', value: 'FechaInicio', sortable: false, align: 'center' },
          { text: 'Estado', value: 'Estado', sortable: false, align: 'center' },
          { text: 'Usuario', value: 'UsuarioNombre', sortable: false, align: 'center' },
          { text: 'Acciones', value: 'acciones', sortable: false, align: 'center' },
        ],
      }
    },

    computed: {
      loadingTestConnection () {
        return this.$store.state.ats.loadingTestConnection
      },
      loadingWsProcess () {
        return this.$store.state.ats.loadingWsProcess
      },
      loadingWsList () {
        return this.$store.state.ats.loadingWsList
      },
      testConnectionStatus () {
        return this.$store.state.ats.testConnectionStatus
      },
      wsProcessStatus () {
        return this.$store.state.ats.wsProcessStatus
      },
      wsList () {
        return this.$store.state.ats.wsList
      },
      hasActiveRows () {
        return this.wsList.some(row => this.isActiveEstado(row.Estado))
      },
      isPolling () {
        return this.pollingTimer !== null
      },
    },

    watch: {
      testConnectionStatus (val) {
        if (val === true) {
          this.connectionTestPassed = true
          this.generateAlertTestSuccess(this.$store.state.ats.testConnectionMessage)
        } else if (val === false) {
          this.connectionTestPassed = false
          this.generateAlertTestError(this.$store.state.ats.testConnectionMessage + '. Verifique las credenciales, posiblemente se actualizaron.')
        }
      },
      hasActiveRows (active) {
        if (active) {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      },
    },

    created () {
      this.fetchCredentials()
      this.refreshList()
    },

    beforeDestroy () {
      this.stopPolling()
      this.$store.dispatch('ats/resetWsProcess')
      this.$store.dispatch('ats/resetTestConnection')
      this.$store.dispatch('ats/resetWsList')
    },

    methods: {
      async fetchCredentials () {
        this.loadingCredentials = true
        const credentials = await this.$store.dispatch('ats/getBridgeCredentials', {
          idCompania: this.userIdCompania,
        })
        this.loadingCredentials = false
        const cred = Array.isArray(credentials) ? credentials[0] : credentials
        if (cred && cred.hasCredentials) {
          this.data.bridgeUser = cred.bridgeUser || ''
          this.data.bridgePassword = cred.bridgePassword || ''
          if (this.data.bridgeUser && this.data.bridgePassword) {
            this.generateAlertTestInfo('Credenciales Bridge WS cargadas automáticamente desde la configuración.')
          }
        } else {
          this.data.bridgeUser = ''
          this.data.bridgePassword = ''
          this.generateAlertTestWarning('No se encontraron credenciales Bridge WS parametrizadas. Ingrese las credenciales manualmente.')
        }
        this.connectionTestPassed = false
      },

      testConnection () {
        if (!this.data.bridgeUser || !this.data.bridgePassword) {
          this.generateAlertTestError('Debe ingresar usuario y clave de Bridge WS')
          return
        }
        this.connectionTestPassed = false
        this.$store.dispatch('ats/testBridgeConnection', {
          idCompania: this.userIdCompania,
          bridgeUser: this.data.bridgeUser,
          bridgePassword: this.data.bridgePassword,
        })
      },

      async processWebService () {
        if (!this.$refs.atsWsForm.validate()) return

        if (!this.$refs.periodSelector.isValid()) {
          this.generateErrorAlert('Debe seleccionar un período (mes o semestre)')
          return
        }

        if (!this.connectionTestPassed) {
          this.generateErrorAlert('Debe realizar el test de conexión exitosamente antes de procesar')
          return
        }

        if (this.hasActiveRows) {
          this.generateErrorAlert('Ya hay una exportación en curso para esta compañía. Espere a que finalice antes de solicitar una nueva.')
          return
        }

        try {
          const result = await this.$store.dispatch('ats/processAtsWebService', {
            fechaInicio: this.data.fechaInicio,
            fechaFin: this.data.fechaFin,
            periodoInformado: this.data.periodoInformado,
            tipoPeriodicidad: this.data.tipoPeriodicidad,
            idCompania: this.userIdCompania,
          })

          if (result && result.Reused) {
            this.generateAlertInfo('Ya hay una exportación en curso para esta compañía. Se hará seguimiento sobre la solicitud existente.')
          } else {
            this.generateAlertSuccess('Solicitud registrada. La exportación se procesará en segundo plano.')
          }

          await this.refreshList()
        } catch (error) {
          this.generateErrorAlert(this.$store.state.ats.wsProcessMessage || error.message)
        }
      },

      async refreshList () {
        await this.$store.dispatch('ats/getAtsWebServiceList', {
          idCompania: this.userIdCompania,
        })
        if (this.hasActiveRows) {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      },

      startPolling () {
        if (this.pollingTimer) return
        this.pollingTimer = setInterval(() => {
          this.$store.dispatch('ats/getAtsWebServiceList', {
            idCompania: this.userIdCompania,
          }).then(() => {
            if (!this.hasActiveRows) {
              this.stopPolling()
            }
          })
        }, POLLING_INTERVAL_MS)
      },

      stopPolling () {
        if (this.pollingTimer) {
          clearInterval(this.pollingTimer)
          this.pollingTimer = null
        }
      },

      async downloadRow (item) {
        if (this.downloadingTrackingId) return
        this.downloadingTrackingId = item.TrackingId
        try {
          const statusResp = await this.$store.dispatch('ats/getAtsWebServiceStatus', {
            trackingId: item.TrackingId,
          })

          if (!statusResp || statusResp.Estado !== 'completed' || !statusResp.ArchivoBase64) {
            this.generateErrorAlert('El archivo aún no está disponible para descargar.')
            await this.refreshList()
            return
          }

          const byteCharacters = atob(statusResp.ArchivoBase64)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const xmlFileName = statusResp.NombreArchivo || item.NombreArchivo || 'ATS_WS_Respuesta.xml'
          const zipFileName = xmlFileName.replace(/\.xml$/i, '.zip')

          const zip = new JSZip()
          zip.file(xmlFileName, byteArray)
          const blob = await zip.generateAsync({ type: 'blob' })
          saveAs(blob, zipFileName)
        } catch (error) {
          this.generateErrorAlert('Error al descargar el archivo: ' + error.message)
        } finally {
          this.downloadingTrackingId = null
        }
      },

      showErrorDetail (item) {
        this.errorDialogItem = item
        this.errorDialog = true
      },

      isActiveEstado (estado) {
        return estado === 'pending' || estado === 'processing'
      },

      estadoColor (estado) {
        switch (estado) {
          case 'completed': return 'green darken-1'
          case 'failed': return 'red darken-1'
          case 'processing': return 'blue darken-1'
          case 'pending':
          default: return 'grey darken-1'
        }
      },

      formatEstado (estado) {
        switch (estado) {
          case 'completed': return 'Completado'
          case 'failed': return 'Fallido'
          case 'processing': return 'Procesando'
          case 'pending': return 'Pendiente'
          default: return estado || '—'
        }
      },

      formatTipoPeriodicidad (tipo) {
        if (!tipo) return ''
        const t = String(tipo).toLowerCase()
        if (t === 'mensual') return 'Mensual'
        if (t === 'semestral') return 'Semestral'
        return tipo
      },

      formatDateOnly (value) {
        if (!value) return '—'
        const dateStr = String(value).substring(0, 10)
        const [y, m, d] = dateStr.split('-')
        if (!y || !m || !d) return value
        return `${d}/${m}/${y}`
      },

      formatDateTime (value) {
        if (!value) return '—'
        const str = String(value).replace('T', ' ')
        const [datePart, timePart = ''] = str.split(' ')
        const [y, m, d] = datePart.split('-')
        if (!y || !m || !d) return value
        const time = timePart.substring(0, 8)
        return `${d}/${m}/${y} ${time}`.trim()
      },

      generateAlertSuccess (msj) {
        this.alertType = 'success'
        this.alertMessage = msj
        this.showAlert = true
        setTimeout(() => {
          this.alertMessage = ''
          this.showAlert = false
        }, 4000)
      },
      generateAlertInfo (msj) {
        this.alertType = 'info'
        this.alertMessage = msj
        this.showAlert = true
        setTimeout(() => {
          this.alertMessage = ''
          this.showAlert = false
        }, 5000)
      },
      generateErrorAlert (msj) {
        this.alertType = 'error'
        this.alertMessage = msj
        this.showAlert = true
        setTimeout(() => {
          this.alertMessage = ''
          this.showAlert = false
        }, 4000)
      },
      generateAlertTestSuccess (msj) {
        this.alertTestType = 'success'
        this.alertTestMessage = msj
        this.showAlertTest = true
        setTimeout(() => {
          this.alertTestMessage = ''
          this.showAlertTest = false
        }, 4000)
      },
      generateAlertTestError (msj) {
        this.alertTestType = 'error'
        this.alertTestMessage = msj
        this.showAlertTest = true
        setTimeout(() => {
          this.alertTestMessage = ''
          this.showAlertTest = false
        }, 4000)
      },
      generateAlertTestInfo (msj) {
        this.alertTestType = 'info'
        this.alertTestMessage = msj
        this.showAlertTest = true
        setTimeout(() => {
          this.alertTestMessage = ''
          this.showAlertTest = false
        }, 4000)
      },
      generateAlertTestWarning (msj) {
        this.alertTestType = 'warning'
        this.alertTestMessage = msj
        this.showAlertTest = true
        setTimeout(() => {
          this.alertTestMessage = ''
          this.showAlertTest = false
        }, 4000)
      },
    },
  }
</script>
